// Light/dark theme choice, a per-device convenience kept in localStorage.
// "system" leaves <html> without data-theme so CSS follows the OS setting
// (packages/ui/src/styles.css); "light"/"dark" set it explicitly. The browser's
// status bar (<meta name="theme-color">) follows: indigo in light, the top
// bar's colour in dark.
import { useSyncExternalStore } from 'react'

export type ThemeChoice = 'system' | 'light' | 'dark'

const KEY = 'ims.theme'
const BAR = { light: '#4f46e5', dark: '#121829' }
const listeners = new Set<() => void>()
const osDark =
  typeof window === 'undefined' ? null : window.matchMedia('(prefers-color-scheme: dark)')

function read(): ThemeChoice {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    return 'system'
  }
}

let current: ThemeChoice = read()

function apply(choice: ThemeChoice) {
  const root = document.documentElement
  if (choice === 'system') delete root.dataset.theme
  else root.dataset.theme = choice
  const dark = choice === 'dark' || (choice === 'system' && (osDark?.matches ?? false))
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', dark ? BAR.dark : BAR.light)
}

/** Applies the saved choice. Call once before the first render. */
export function initTheme() {
  apply(current)
  osDark?.addEventListener('change', () => apply(current))
}

export function setTheme(choice: ThemeChoice) {
  current = choice
  try {
    if (choice === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, choice)
  } catch {
    // Storage blocked: the choice still applies for this session.
  }
  apply(choice)
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useTheme(): [ThemeChoice, (choice: ThemeChoice) => void] {
  return [useSyncExternalStore(subscribe, () => current), setTheme]
}
